import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';

const GalleryDetail = () => {
  const { slug } = useParams();
  const [album, setAlbum] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await API.get(`/galleries/public/${slug}`);
        setAlbum(res.data.data || null);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [slug]);

  if (loading) return <div className="loading-screen"><i className="fas fa-spinner fa-spin"></i></div>;

  if (!album) {
    return (
      <section className="section py-20">
        <div className="container text-center">
          <h2 className="text-primary text-2xl font-bold">Album not found</h2>
        </div>
      </section>
    );
  }

  const albumImage = album.images && album.images.length > 0 && album.images[0].image
    ? `${UPLOADS_URL}/${album.images[0].image}`
    : null;

  return (
    <>
      <SEO
        title={album.title}
        description={album.description || `Photo gallery - ${album.title}`}
        ogImage={albumImage}
      />

      {/* Title */}
      <section className="pt-10 pb-6 bg-light">
        <div className="container text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-primary">{album.title}</h1>
        </div>
      </section>

      {/* Images */}
      <section className="section py-10 md:py-12 bg-light">
        <div className="container">
          {(!album.images || album.images.length === 0) ? (
            <div className="empty-state text-center p-16 text-body">
              <i className="fas fa-images text-5xl opacity-50 mb-4"></i>
              <p className="text-muted text-center">No photos in this album yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {album.images.map((img, idx) => (
                <Reveal key={img.id} delayMs={Math.min(idx * 40, 400)}>
                  <div
                    className="relative group cursor-pointer overflow-hidden rounded-lg shadow-sm hover:shadow-lg transition-all duration-300"
                    onClick={() => setLightbox(img)}
                  >
                    <img
                      src={`${UPLOADS_URL}/${img.image}`}
                      alt={img.title || `${album.title} photo ${idx + 1}`}
                      className="w-full h-48 md:h-56 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/40 transition-all duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                        <i className="fas fa-search-plus text-lg"></i>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[999] bg-black/85 flex items-center justify-center p-4 cursor-pointer backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition-colors z-10"
            onClick={() => setLightbox(null)}
          >
            <i className="fas fa-times"></i>
          </button>
          <img
            src={`${UPLOADS_URL}/${lightbox.image}`}
            alt={lightbox.title || ''}
            className="max-w-[92vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default GalleryDetail;
